 import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { db } from '../../firebaseConfig';
import { collection, doc, getDoc, getDocs, query, where, documentId } from 'firebase/firestore';
import { updateUserProfile } from './authSlice';

// Configurable TTL (defaults to 30 minutes)
const DEFAULT_TTL_MS = Number(process.env.REACT_APP_USER_CACHE_TTL_MS || 30 * 60 * 1000);
const MAX_IN_QUERY = 30;
const DEBUG_USER_CACHE = process.env.REACT_APP_DEBUG_USER_CACHE === 'true';

const initialState = {
  entities: {}, // { [userId]: { data, fetchedAt } }
  status: {},   // { [userId]: 'idle'|'loading'|'succeeded'|'failed' }
  error: {},    // { [userId]: string|null }
  ttlMs: DEFAULT_TTL_MS,
};

const isFresh = (entry, ttlMs) => {
  if (!entry || !entry.fetchedAt) return false;
  try {
    const fetched = typeof entry.fetchedAt === 'number' ? entry.fetchedAt : new Date(entry.fetchedAt).getTime();
    const fresh = Date.now() - fetched < ttlMs;
    return fresh;
  } catch (_) { return false; }
};

export const fetchUserIfNeeded = createAsyncThunk(
  'usersCache/fetchUserIfNeeded',
  async ({ userId, force = false }, { getState, dispatch, rejectWithValue }) => {
    const state = getState();
    const existing = selectUserById(state, userId);
    
    // If we have data and it's not forced, check TTL
    if (existing?.data && !force) {
      const age = Date.now() - (existing.fetchedAt || 0);
      if (age < DEFAULT_TTL_MS) {
        if (DEBUG_USER_CACHE) console.debug('[usersCache] using cached user', userId, 'age', Math.round(age / 1000), 's');
        return { userId, data: existing.data, fromCache: true };
      }
    }
    
    if (DEBUG_USER_CACHE) console.debug('[usersCache] fetching user', userId, force ? '(forced)' : '(cache miss/expired)');
    
    // Use request deduplication to prevent duplicate API calls
    const { requestDeduplicator } = await import('../../utils/performanceOptimizations');
    const requestKey = `fetchUser-${userId}`;
    
    return requestDeduplicator.request(requestKey, async () => {
      try {
        const now = Date.now();
        const ref = doc(db, 'users', userId);
        const snap = await getDoc(ref);
        if (!snap.exists()) {
          if (DEBUG_USER_CACHE) console.debug('[usersCache] user not found', userId);
          return { userId, data: null };
        }
        const data = { id: snap.id, ...snap.data() };
        if (DEBUG_USER_CACHE) console.debug('[usersCache] fetched user', userId);
        return { userId, data, fetchedAt: now };
      } catch (e) {
        console.error('[usersCache] fetch error for', userId, e);
        return rejectWithValue(e.message);
      }
    });
  }
);

export const fetchUsersByIds = createAsyncThunk(
  'usersCache/fetchUsersByIds',
  async ({ userIds }, { getState, rejectWithValue }) => {
    try {
      const unique = Array.from(new Set((userIds || []).filter(Boolean)));
      if (unique.length === 0) return { results: {} };
      const state = getState();
      const { entities, ttlMs } = state.usersCache || {};
      const now = Date.now();
      const toFetch = unique.filter((id) => !isFresh(entities?.[id], ttlMs));
      if (toFetch.length === 0) return { results: {} };

      const usersCol = collection(db, 'users');
      const chunks = [];
      for (let i = 0; i < toFetch.length; i += MAX_IN_QUERY) {
        chunks.push(toFetch.slice(i, i + MAX_IN_QUERY));
      }

      const results = {};
      for (const chunk of chunks) {
        const q = query(usersCol, where(documentId(), 'in', chunk));
        const snap = await getDocs(q);
        snap.forEach((docSnap) => {
          results[docSnap.id] = { id: docSnap.id, ...docSnap.data() };
        });
        if (DEBUG_USER_CACHE) console.debug('[usersCache] fetched chunk size', chunk.length, 'results so far', Object.keys(results).length);
      }
      return { results, fetchedAt: now };
    } catch (e) {
      if (DEBUG_USER_CACHE) console.debug('[usersCache] fetchUsersByIds error', e?.message);
      return rejectWithValue(e?.message || 'fetchUsersByIds failed');
    }
  }
);

const usersCacheSlice = createSlice({
  name: 'usersCache',
  initialState,
  reducers: {
    invalidateUser(state, action) {
      const id = action.payload;
      if (state.entities[id]) delete state.entities[id];
      if (state.status[id]) delete state.status[id];
      if (state.error[id]) delete state.error[id];
    },
    setTtl(state, action) {
      state.ttlMs = Number(action.payload) || DEFAULT_TTL_MS;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserIfNeeded.pending, (state, action) => {
        const { userId } = action.meta.arg || {};
        if (userId) state.status[userId] = 'loading';
      })
      .addCase(fetchUserIfNeeded.fulfilled, (state, action) => {
        const { userId, data } = action.payload || {};
        if (!userId) return;
        state.entities[userId] = data ? { data, fetchedAt: Date.now() } : { data: null, fetchedAt: Date.now() };
        state.status[userId] = 'succeeded';
        state.error[userId] = null;
        if (DEBUG_USER_CACHE) console.debug('[usersCache] stored user in cache', userId, Boolean(data));
      })
      .addCase(fetchUserIfNeeded.rejected, (state, action) => {
        const { userId } = action.meta.arg || {};
        if (!userId) return;
        state.status[userId] = 'failed';
        state.error[userId] = action.payload || action.error?.message || 'Unknown error';
        if (DEBUG_USER_CACHE) console.debug('[usersCache] mark failed for', userId, state.error[userId]);
      })
      .addCase(fetchUsersByIds.pending, (state) => {
        // no-op
      })
      .addCase(fetchUsersByIds.fulfilled, (state, action) => {
        const { results, fetchedAt } = action.payload || {};
        if (!results) return;
        const ts = fetchedAt || Date.now();
        Object.keys(results).forEach((id) => {
          state.entities[id] = { data: results[id], fetchedAt: ts };
          state.status[id] = 'succeeded';
          state.error[id] = null;
        });
        if (DEBUG_USER_CACHE) console.debug('[usersCache] stored batch users', Object.keys(results).length);
      })
      .addCase(fetchUsersByIds.rejected, (state, action) => {
        // You can store a global error if desired
      })
      // Listen to auth profile updates to keep user cache in sync
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        const updatedUserData = action.payload;
        const userId = updatedUserData?.id;
        
        if (userId && state.entities[userId]) {
          // Update the cached user data with the new profile information
          state.entities[userId] = {
            ...state.entities[userId],
            data: {
              ...state.entities[userId].data,
              ...updatedUserData,
              updatedAt: new Date().toISOString()
            },
            fetchedAt: Date.now() // Mark as fresh
          };
          
          if (DEBUG_USER_CACHE) {
            console.debug('[usersCache] Updated cached user from auth profile update:', userId);
          }
        }
      });
  },
});

export const { invalidateUser, setTtl } = usersCacheSlice.actions;

// Selectors
export const selectUsersEntities = (state) => state.usersCache?.entities || {};
export const makeSelectUserById = () => createSelector(
  [selectUsersEntities, (_, userId) => userId],
  (entities, userId) => entities[userId]?.data || null
);
export const selectUserById = (state, userId) => (state.usersCache?.entities?.[userId]?.data) || null;

export default usersCacheSlice.reducer;
